// THE WEBSITES' STATE IN AN AI ANSWER — the `project-status` view
// (functions/canvasAnswer.js canvasAiViews, functions/canvasStatus.js; the
// shell mounts it in the assistant's thread and on a Fly page with scope
// { params, embedded }). Read-only. EVERY website (no project named and no
// website on screen: more than one website, or a project on screen that is
// not a website, an app canvas, whose only website never stands in for it):
// one row per site, its name and its production state chip (the house status
// chip — live, never published, not available); a row opens the site in the
// card, Back returns. ONE website (a project named, the website on screen,
// or, with nothing on screen, the workspace's only website): the site and
// whether a visitor gets it, what each environment serves, the newest
// versions. No publish control: a change is the page's "Do it". While
// reading: three row stand-ins; no such site, or no website at all: one
// sentence — for a name that is a project of the workspace but not a
// website, or an App Store app that is no project here, the sentence says so
// with its name.
export const ProjectStatusView = {
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  height: '100%',
  minHeight: '0',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  dataProjectStatus: true,
  state: {
    site: null,
    sites: null,
    total: 0,
    pick: null,
    live: null,
    envs: null,
    versions: null,
    failed: false,
    none: false,
    // The asked name when it is no website (functions/canvasAnswer.js
    // canvasAnswerNamed): a project of this workspace that is not a website
    // (an app, a module, a library), or an App Store app that is no project
    // here.
    notWebsite: null,
    storeApp: null
  },
  // EMPTY (the views contract, workspace functions/assistantAppViews.js): no
  // such site, or no website at all — the one sentence is all the view shows.
  empty: (el, s) => (s.failed ? true : s.site || Array.isArray(s.sites) ? false : null),
  // RESOLVED PARAMS (the views contract, workspace functions/
  // assistantAppViews.js): the site the card SHOWS — a Fly page's next ask
  // carries it, so a follow-up stays on this site. null while reading and
  // while the list of every website shows (no one site is picked).
  resolvedParams: (el, s) => (s.site ? { project: s.site.key } : null),
  onRender: (el) => el.call('canvasStatusLoad', el),
  // The installed apps decide which projects are websites, and the project
  // list can land after the view (a page restored at boot): the read runs
  // again when either changes — its own signature keeps it to once per list.
  // The App Store list can land after the view too: when it (or the project
  // list) changes what the asked name means, the read runs again
  // (canvasAnswerNamedMoved).
  subscribeTo: ['workspaceApps', '_projectsForActiveOrg', '_wsAppsCatalog'],
  onUpdate: (el) => el.call('canvasStatusLoad', el),

  ProjectStatusSkeleton: {
    flow: 'y',
    gap: 'Z',
    ariaBusy: 'true',
    hide: (el, s) => !!s.site || !!s.failed || Array.isArray(s.sites),
    Skeleton_1: { width: '60%', height: 'B', round: 'radiusControl' },
    Skeleton_2: { height: 'C', round: 'radiusControl' },
    Skeleton_3: { height: 'C', round: 'radiusControl' }
  },

  ProjectStatusNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => !s.failed,
    text: (el, s) => {
      void s.root.lang
      if (s.notWebsite) {
        return String(el.call('polyglot', 'canvas.status.notWebsite')).replace(
          '{name}',
          () => s.notWebsite
        )
      }
      if (s.storeApp) {
        return String(el.call('polyglot', 'canvas.status.storeApp')).replace(
          '{name}',
          () => s.storeApp
        )
      }
      return el.call('polyglot', s.none ? 'canvas.status.none' : 'canvas.status.missing')
    }
  },

  ProjectStatusHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    minWidth: '0',
    hide: (el, s) => !s.site,
    ProjectStatusTitle: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      minWidth: '0',
      // Back to every website — only when a row of the list opened this one.
      CompactIconButton: {
        icon: 'arrowLeft',
        hide: (el, s) => !s.pick,
        ariaLabel: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'canvas.status.back')
        },
        title: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'canvas.status.back')
        },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('canvasStatusPick', el, null)
        }
      },
      ProjectStatusName: {
        tag: 'h4',
        margin: '0',
        minWidth: '0',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => (s.site ? s.site.name : '')
      }
    },
    // "Is our site live?" — what a visitor of the production site gets: the
    // house status chip, its state the answer (a new answer is a new chip;
    // the live region around it is there before it changes).
    ProjectStatusLive: {
      flexShrink: '0',
      role: 'status',
      childExtends: 'AppStatusChip',
      childrenAs: 'state',
      children: (el, s) => {
        const live = s.live || { status: null, label: 'canvas.status.live.checking' }
        return [
          {
            key: 'live-' + String(live.label).split('.').pop(),
            status: live.status,
            label: live.label
          }
        ]
      }
    }
  },

  // EVERY WEBSITE: one row per site — its name and its production state.
  ProjectStatusSites: {
    tag: 'ul',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    flow: 'y',
    gap: 'X',
    hide: (el, s) => !!s.site || !Array.isArray(s.sites) || !s.sites.length,
    childrenAs: 'state',
    childExtends: 'ProjectStatusSiteRow',
    children: (el, s) =>
      (Array.isArray(s.sites) ? s.sites : []).map((r) => ({
        key: 'site-' + r.key,
        siteKey: r.key,
        name: r.name,
        live: r.live
      }))
  },

  // The first SITES_MAX of more: says so, never a silent cut.
  ProjectStatusMore: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => !!s.site || !Array.isArray(s.sites) || !(s.total > s.sites.length),
    text: (el, s) => {
      void s.root.lang
      return String(el.call('polyglot', 'canvas.status.more'))
        .replace('{n}', String(Array.isArray(s.sites) ? s.sites.length : 0))
        .replace('{total}', String(s.total || 0))
    }
  },

  ProjectStatusEnvs: {
    tag: 'ul',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    flow: 'y',
    gap: 'X',
    hide: (el, s) => !s.site || !Array.isArray(s.envs),
    childrenAs: 'state',
    childExtends: 'ProjectStatusRow',
    childProps: { tag: 'li' },
    children: (el, s) =>
      (Array.isArray(s.envs) ? s.envs : []).map((e) => ({
        key: 'env-' + e.key,
        title: e.label,
        meta: e.serves,
        value: e.value,
        on: e.on
      }))
  },

  ProjectStatusVersionsTitle: {
    tag: 'h5',
    margin: 'Z 0 0',
    fontSize: 'Z',
    fontWeight: '500',
    color: 'caption',
    hide: (el, s) => !s.site || !Array.isArray(s.versions) || !s.versions.length,
    text: '{{ canvas.status.versions | polyglot }}'
  },

  ProjectStatusVersions: {
    tag: 'ul',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    flow: 'y',
    gap: 'X',
    hide: (el, s) => !s.site || !Array.isArray(s.versions) || !s.versions.length,
    childrenAs: 'state',
    childExtends: 'ProjectStatusRow',
    childProps: { tag: 'li' },
    children: (el, s) =>
      (Array.isArray(s.versions) ? s.versions : []).map((v) => ({
        key: v.key,
        title: v.message || v.value,
        meta: [v.value, v.author, v.at ? el.call('timeAgo', v.at) : ''].filter(Boolean).join(' · '),
        value: '',
        on: true
      }))
  }
}

// One website of the every-website list: its name and its production state
// (the house status chip; "Checking…" while it reads). A button: it opens
// the site in the card (its environments, its versions).
export const ProjectStatusSiteRow = {
  tag: 'li',
  ProjectStatusSiteOpen: {
    tag: 'button',
    type: 'button',
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    width: '100%',
    minWidth: '0',
    padding: 'Y Z',
    border: 'none',
    round: 'radiusControl',
    background: 'overlay',
    color: 'title',
    fontSize: 'Z',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background-color A defaultBezier',
    ':hover': { background: 'hover' },
    ':active': { background: 'selected' },
    ariaLabel: (el, s) => {
      void s.root.lang
      return String(el.call('polyglot', 'canvas.status.openSite')).replace(
        '{name}',
        () => s.name || ''
      )
    },
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('canvasStatusPick', el, s.siteKey)
    },
    ProjectStatusSiteName: {
      tag: 'span',
      minWidth: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.name || ''
    },
    ProjectStatusSiteLive: {
      flexShrink: '0',
      childExtends: 'AppStatusChip',
      childrenAs: 'state',
      children: (el, s) => {
        const live = s.live || { status: null, label: 'canvas.status.live.checking' }
        return [
          {
            key: 'live-' + String(live.label).split('.').pop(),
            status: live.status,
            label: live.label
          }
        ]
      }
    }
  }
}

// One row of the status card: a title and a quiet second part on one line —
// an environment ("Production · Published release") or a version ("Fix the
// header · 1.4.2 · Nika · 2 hours ago"). `title` / `meta` are bundle keys or
// plain words (polyglot returns a non-key verbatim); `value` fills a
// `{value}` in the meta. An environment that serves nothing reads quieter.
export const ProjectStatusRow = {
  flow: 'x',
  align: 'baseline space-between',
  gap: 'Z',
  minWidth: '0',
  padding: 'Y Z',
  round: 'radiusControl',
  background: 'overlay',
  fontSize: 'Z',
  isOff: (el, s) => s.on === false,
  '.isOff': { opacity: '0.6' },

  ProjectStatusRowTitle: {
    tag: 'span',
    minWidth: '0',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => {
      void s.root.lang
      return el.call('polyglot', s.title || '')
    }
  },
  ProjectStatusRowMeta: {
    tag: 'span',
    flexShrink: '0',
    maxWidth: '60%',
    color: 'caption',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => {
      void s.root.lang
      return String(el.call('polyglot', s.meta || '')).replace('{value}', () => s.value || '')
    }
  }
}
